import type { IAndroidBrightnessStatus } from '@/helpers/parseAndroidBrightnessStatus'
import { parseAndroidBrightnessStatus } from '@/helpers/parseAndroidBrightnessStatus'
import { isTauriRuntime } from '@/helpers/resolvedPlayerAppVersion'
import { getPlatform } from '@/platform'

export type { IAndroidBrightnessStatus }

function isAndroidTauri(): boolean {
  return isTauriRuntime() && getPlatform() === 'android'
}

/** Android-only: effective brightness, mode, control support and apply method. */
export async function fetchAndroidBrightnessStatus(): Promise<IAndroidBrightnessStatus | null> {
  if (!isAndroidTauri()) {
    return null
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    return parseAndroidBrightnessStatus(await invoke<unknown>('plugin:lucimark-android|getBrightnessStatus'))
  }
  catch {
    return null
  }
}

/**
 * Android-only: applies a Studio brightness target (1–100) or releases control (`null`).
 * Resolves false when not on Android or the native call failed (never throws).
 */
export async function applyAndroidBrightness(percent: number | null): Promise<boolean> {
  if (!isAndroidTauri()) {
    return false
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    await invoke('plugin:lucimark-android|setBrightness', { percent })
    return true
  }
  catch (err) {
    console.warn('[brightness] setBrightness failed', err)
    return false
  }
}

/**
 * Android-only: hands brightness back to Android's adaptive mode. Resolves true only when the device
 * really switched (Device Owner or `WRITE_SETTINGS`, APK with the native support); false otherwise, so the
 * caller keeps a fixed fallback. An APK without the support treats the call as a release and answers
 * nothing, which reads as false.
 */
export async function applyAndroidAutomaticBrightness(): Promise<boolean> {
  if (!isAndroidTauri()) {
    return false
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    const result = await invoke<{ applied?: unknown } | null | undefined>('plugin:lucimark-android|setBrightness', { automatic: true })
    return result?.applied === true
  }
  catch (err) {
    console.warn('[brightness] automatic brightness failed', err)
    return false
  }
}

/** Android-only: opens "Modify system settings" so WRITE_SETTINGS can be granted. */
export async function openAndroidWriteSettings(): Promise<boolean> {
  if (!isAndroidTauri()) {
    return false
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    await invoke('plugin:lucimark-android|openWriteSettings')
    return true
  }
  catch {
    return false
  }
}
